import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type * as Y from "yjs";
import type { FactionId } from "@/data/factions.ts";
import type { RoleId } from "@/data/roles.ts";
import {
  applyCommandAction,
  COMMANDER_GRACE_MS,
  currentCommanderId,
  isVoteExpired,
  tallyVotes,
  type CommandAction,
  type CommandState,
  type Vote,
} from "./command.ts";
import type { Identity } from "./identity.ts";
import { syncRoomName } from "./roomCode.ts";
import type {
  Marker,
  MarkerKind,
  Order,
  Player,
  Route,
  SessionMeta,
  Squad,
} from "./schemas.ts";
import type { GameCoord } from "@/engine/coords.ts";
import { DEFAULT_MAP_ID } from "@/data/maps.ts";
import { assignSquadTags, type OrderKind } from "@/data/squads.ts";
import {
  amendOrder,
  assignPlayerSquad,
  cancelOrder,
  castVote,
  createSquad as createSquadRecord,
  currentOrderFor,
  deleteMarker as deleteMarkerRecord,
  clearMapTactical,
  deleteRoute as deleteRouteRecord,
  getSessionDoc,
  initialiseSession,
  issueOrder,
  putMarker as putMarkerRecord,
  putRoute as putRouteRecord,
  readCommandState,
  readMarkers,
  readMeta,
  readOrders,
  readPlayers,
  readRoutes,
  readSquads,
  readVotes,
  removePlayer as removePlayerRecord,
  setPlayerVehicle,
  setOrderStatus,
  setSessionFaction,
  setSessionMap,
  setSquadLead as setSquadLeadRecord,
  touchPlayer,
  upsertPlayer,
  waitForSessionSync,
  withdrawVote,
  writeCommandState,
} from "./sessionDoc.ts";
import { createSyncProvider, type SyncProvider, type SyncState } from "./syncProvider.ts";
import { useAwarenessStates, useYjsValue } from "./useYjs.ts";

/** Presence record broadcast over awareness. */
export interface PresenceEntry {
  readonly id: string;
  readonly callsign: string;
  readonly faction: FactionId;
  readonly role: RoleId;
}

function parsePresence(state: unknown): PresenceEntry | undefined {
  if (typeof state !== "object" || state === null) return undefined;
  const player = (state as Record<string, unknown>)["player"];
  if (typeof player !== "object" || player === null) return undefined;

  const record = player as Record<string, unknown>;
  const id = record["id"];
  const callsign = record["callsign"];
  if (typeof id !== "string" || typeof callsign !== "string") return undefined;

  return {
    id,
    callsign,
    faction: record["faction"] as FactionId,
    role: record["role"] as RoleId,
  };
}

export interface SessionApi {
  readonly code: string;
  readonly ready: boolean;
  readonly sync: SyncState;
  readonly meta: SessionMeta | undefined;
  readonly players: readonly Player[];
  readonly present: readonly PresenceEntry[];
  readonly commandState: CommandState;
  readonly votes: readonly Vote[];
  readonly markers: readonly Marker[];
  readonly routes: readonly Route[];
  readonly orders: readonly Order[];
  /** All orders across all maps, for history and cross-map counts. */
  readonly allOrders: readonly Order[];
  /** All markers across all maps, for cross-map indicator dots. */
  readonly allMarkers: readonly Marker[];
  /** All routes across all maps, for cross-map indicator dots. */
  readonly allRoutes: readonly Route[];
  readonly squads: readonly Squad[];
  /**
   * One tag per squad id, guaranteed unique across the whole session — see
   * `assignSquadTags`. Every place that shows a squad's tag should read it
   * from here rather than deriving one locally, so the same squad never
   * reads as "R" in one component and "R2" in another.
   */
  readonly squadTags: ReadonlyMap<string, string>;
  readonly commanderId: string | null;
  readonly isCommander: boolean;
  readonly tally: ReturnType<typeof tallyVotes>;
  readonly myVote: Vote | undefined;
  /** Squad the local player belongs to. Empty when unassigned. */
  readonly mySquad: string;
  /** The order the local player's squad is currently working to. */
  readonly myOrder: Order | undefined;
  readonly isSquadLead: boolean;
  // Declared as properties rather than methods so they carry no implicit
  // `this`, which is what lets them be passed straight to an onClick.
  readonly claimCommand: () => void;
  readonly contestCommand: () => void;
  readonly resignCommand: () => void;
  readonly vote: (inFavour: boolean) => void;
  readonly clearVote: () => void;
  /**
   * Change the session's shared map or faction. Writes to `meta`, so every
   * peer follows — a squad staring at a different theatre than the one the
   * commander is issuing grid references for is worse than no map tool.
   * Callers are expected to gate this to the commander in the UI.
   */
  readonly setMap: (mapId: string) => void;
  readonly setFaction: (faction: FactionId) => void;
  readonly joinSquad: (squadId: string) => void;
  /** Create a brand-new squad with the given name, and join it. Returns its id. */
  readonly createSquad: (name: string) => string;
  /** Hand a squad's lead to a specific member, e.g. when the lead has gone dark. */
  readonly setSquadLead: (squadId: string, playerId: string) => void;
  /** Drop a stale player from the roster entirely. Commander housekeeping only. */
  readonly removePlayer: (playerId: string) => void;
  /** Set what vehicle you are currently in (empty = on foot). */
  readonly setVehicle: (vehicle: string) => void;
  /** Drop a marker on the shared map. Anyone can call out a contact or a hazard. */
  /** Draw a flight path or movement route on the map. */
  readonly putRoute: (waypoints: readonly GameCoord[], label?: string) => void;
  readonly deleteRoute: (routeId: string) => void;
  /**
   * Start a new match on the current map: cancel active orders, remove
   * markers and routes. Squads, command and the roster are untouched.
   */
  readonly newMatch: () => void;
  readonly putMarker: (input: {
    kind: MarkerKind;
    at: GameCoord;
    label?: string;
    radiusMeters?: number;
    weaponId?: string;
    threat?: boolean;
  }) => void;
  readonly deleteMarker: (markerId: string) => void;
  readonly issue: (input: {
    squadId: string;
    kind: OrderKind;
    place?: string;
    note?: string;
    at?: { x: number; y: number };
  }) => void;
  /** Squad lead marks the order done. It moves to history; the squad is idle again. */
  readonly complete: (orderId: string) => void;
  readonly amend: (
    orderId: string,
    changes: Partial<Pick<Order, "kind" | "place" | "note" | "at" | "squadId">>,
  ) => void;
  readonly cancel: (orderId: string) => void;
  readonly doc: Y.Doc | null;
}

const HEARTBEAT_MS = 20_000;

/**
 * Everything a session needs, in one hook.
 *
 * Joins the document, announces presence, keeps the roster warm, and resolves
 * command votes. Command resolution runs on every connected client and writes
 * the same result, which Yjs merges — no elected authority is needed to count
 * an election, which is convenient given the election is what decides who has
 * authority.
 */
export function useSession(
  code: string | null,
  identity: Identity,
  options: { readonly mapId: string; readonly websocketUrl?: string | undefined },
): SessionApi {
  const [doc, setDoc] = useState<Y.Doc | null>(null);
  const [ready, setReady] = useState(false);
  const [sync, setSync] = useState<SyncState>({
    status: "disconnected",
    peerCount: 0,
    error: null,
  });

  const providerRef = useRef<SyncProvider | null>(null);
  const [awareness, setAwareness] = useState<SyncProvider["awareness"] | null>(null);

  // --- open the document and connect ------------------------------------

  useEffect(() => {
    if (!code) {
      setDoc(null);
      setReady(false);
      return;
    }

    let cancelled = false;
    const sessionDoc = getSessionDoc(code);
    setDoc(sessionDoc);

    const provider = createSyncProvider({
      roomName: syncRoomName(code),
      doc: sessionDoc,
      websocketUrl: options.websocketUrl,
    });
    providerRef.current = provider;
    setAwareness(provider.awareness);

    const unsubscribe = provider.subscribe(setSync);

    void (async () => {
      // Local data first: the session must be usable before the network is.
      await waitForSessionSync(code);
      if (cancelled) return;

      initialiseSession(sessionDoc, code, options.mapId, identity.faction);
      setReady(true);

      await provider.connect();
    })();

    return () => {
      cancelled = true;
      unsubscribe();
      provider.disconnect();
      providerRef.current = null;
      setAwareness(null);
      setReady(false);
    };
    // Reconnecting on every identity tweak would drop the session, so only the
    // code and the transport matter here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, options.websocketUrl]);

  // --- announce who we are ----------------------------------------------

  useEffect(() => {
    if (!awareness || !doc || !ready) return;

    awareness.setLocalStateField("player", {
      id: identity.id,
      callsign: identity.callsign,
      faction: identity.faction,
      role: identity.role,
    });

    upsertPlayer(doc, {
      id: identity.id,
      callsign: identity.callsign,
      faction: identity.faction,
      role: identity.role,
    });
  }, [awareness, doc, ready, identity]);

  // --- keep the roster warm ---------------------------------------------

  useEffect(() => {
    if (!doc || !ready) return;
    const timer = setInterval(() => touchPlayer(doc, identity.id), HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [doc, ready, identity.id]);

  // --- shared state ------------------------------------------------------

  const players = useYjsValue<Player[]>(doc, "players", readPlayers, []);
  const commandState = useYjsValue<CommandState>(doc, "command", readCommandState, {
    kind: "vacant",
  });
  const votes = useYjsValue<Vote[]>(doc, "votes", readVotes, []);
  const allMarkers = useYjsValue<Marker[]>(doc, "markers", readMarkers, []);
  const allRoutes = useYjsValue<Route[]>(doc, "routes", readRoutes, []);
  const allOrders = useYjsValue<Order[]>(doc, "orders", readOrders, []);
  const squads = useYjsValue<Squad[]>(doc, "squads", readSquads, []);
  const meta = useYjsValue<SessionMeta | undefined>(doc, "meta", readMeta, undefined);

  // Tactical data is per-map. Records with an empty mapId predate this
  // feature and are shown on whichever map is currently active (a soft,
  // non-destructive migration that does not rewrite existing data).
  const activeMapId = meta?.mapId ?? DEFAULT_MAP_ID;
  const markers = useMemo(
    () => allMarkers.filter((m) => (m.mapId || activeMapId) === activeMapId),
    [allMarkers, activeMapId],
  );
  const routes = useMemo(
    () => allRoutes.filter((r) => (r.mapId || activeMapId) === activeMapId),
    [allRoutes, activeMapId],
  );
  const orders = useMemo(
    () => allOrders.filter((o) => (o.mapId || activeMapId) === activeMapId),
    [allOrders, activeMapId],
  );

  // Oldest first and nowhere else: a squad's tag must not depend on which
  // component happens to be looking, or the same squad reads differently on
  // the map than in the roster.
  const squadTags = useMemo(
    () => assignSquadTags([...squads].sort((a, b) => a.createdAt - b.createdAt)),
    [squads],
  );

  const present = useAwarenessStates(awareness, parsePresence);

  // Everyone connected can vote. Presence, not the roster, because a player who
  // closed the tab should not still count towards a majority.
  const eligibleCount = Math.max(1, present.length);
  const tally = useMemo(() => tallyVotes(votes, eligibleCount), [votes, eligibleCount]);

  const commanderId = currentCommanderId(commandState);
  const myVote = useMemo(
    () => votes.find((entry) => entry.voterId === identity.id),
    [votes, identity.id],
  );

  // --- command actions ---------------------------------------------------

  const dispatch = useCallback(
    (action: CommandAction) => {
      if (!doc) return;
      const outcome = applyCommandAction(
        { state: readCommandState(doc), votes: readVotes(doc), eligibleCount },
        action,
      );
      if (outcome.rejected) {
        console.info("Command action refused:", outcome.rejected);
        return;
      }
      writeCommandState(doc, outcome.state, outcome.clearVotes);
    },
    [doc, eligibleCount],
  );

  const claimCommand = useCallback(
    () => dispatch({ type: "claim", playerId: identity.id, at: Date.now() }),
    [dispatch, identity.id],
  );
  /**
   * Contesting is itself a vote in favour: nobody calls an election they do
   * not want to win. Opening the ballot clears it, so the challenger's vote is
   * cast immediately afterwards rather than being lost.
   */
  const contestCommand = useCallback(() => {
    if (!doc) return;
    const before = readCommandState(doc);
    dispatch({ type: "contest", playerId: identity.id, at: Date.now() });

    const after = readCommandState(doc);
    if (after.kind === "contested" && after.challengerId === identity.id) {
      castVote(doc, identity.id, true);
    } else if (before.kind === "vacant") {
      // The chair was empty, so this was a straight claim. No ballot needed.
    }
  }, [doc, dispatch, identity.id]);
  const resignCommand = useCallback(
    () => dispatch({ type: "resign", playerId: identity.id, at: Date.now() }),
    [dispatch, identity.id],
  );

  const vote = useCallback(
    (inFavour: boolean) => {
      if (!doc) return;
      castVote(doc, identity.id, inFavour);
    },
    [doc, identity.id],
  );

  const setMap = useCallback(
    (mapId: string) => {
      if (!doc) return;
      setSessionMap(doc, mapId);
    },
    [doc],
  );

  const setFaction = useCallback(
    (faction: FactionId) => {
      if (!doc) return;
      setSessionFaction(doc, faction);
    },
    [doc],
  );

  // --- squads and orders -------------------------------------------------

  const joinSquad = useCallback(
    (squadId: string) => {
      if (!doc) return;
      assignPlayerSquad(doc, identity.id, squadId);
    },
    [doc, identity.id],
  );

  const createSquad = useCallback(
    (name: string): string => {
      if (!doc) return "";
      return createSquadRecord(doc, identity.id, name);
    },
    [doc, identity.id],
  );

  const setSquadLead = useCallback(
    (squadId: string, playerId: string) => {
      if (!doc) return;
      setSquadLeadRecord(doc, squadId, playerId);
    },
    [doc],
  );

  const removePlayer = useCallback(
    (playerId: string) => {
      if (!doc) return;
      removePlayerRecord(doc, playerId);
    },
    [doc],
  );

  const setVehicle = useCallback(
    (vehicle: string) => {
      if (!doc) return;
      setPlayerVehicle(doc, identity.id, vehicle);
    },
    [doc, identity.id],
  );

  const putMarker = useCallback(
    (input: {
      kind: MarkerKind;
      at: GameCoord;
      label?: string;
      radiusMeters?: number;
      weaponId?: string;
      threat?: boolean;
    }) => {
      if (!doc) return;
      putMarkerRecord(doc, {
        id: crypto.randomUUID(),
        kind: input.kind,
        at: input.at,
        label: input.label ?? "",
        createdBy: identity.id,
        createdAt: Date.now(),
        threat: input.threat ?? false,
        mapId: activeMapId,
        ...(input.radiusMeters !== undefined ? { radiusMeters: input.radiusMeters } : {}),
        ...(input.weaponId !== undefined ? { weaponId: input.weaponId } : {}),
      });
    },
    [doc, identity.id, activeMapId],
  );

  const deleteMarker = useCallback(
    (markerId: string) => {
      if (!doc) return;
      deleteMarkerRecord(doc, markerId);
    },
    [doc],
  );

  const putRoute = useCallback(
    (waypoints: readonly GameCoord[], label?: string) => {
      if (!doc || waypoints.length < 2) return;
      putRouteRecord(doc, {
        id: crypto.randomUUID(),
        label: label ?? "",
        waypoints: waypoints.map((point) => ({ x: point.x, y: point.y })),
        createdBy: identity.id,
        createdAt: Date.now(),
        movement: "flight",
        mapId: activeMapId,
      });
    },
    [doc, identity.id, activeMapId],
  );

  const deleteRoute = useCallback(
    (routeId: string) => {
      if (!doc) return;
      deleteRouteRecord(doc, routeId);
    },
    [doc],
  );

  const newMatch = useCallback(() => {
    if (!doc) return;
    clearMapTactical(doc, activeMapId);
  }, [doc, activeMapId]);

  const mySquad = useMemo(
    () => players.find((player) => player.id === identity.id)?.squad ?? "",
    [players, identity.id],
  );

  const myOrder = useMemo(
    () => (mySquad ? currentOrderFor(orders, mySquad) : undefined),
    [orders, mySquad],
  );

  const isSquadLead = useMemo(
    () => squads.some((squad) => squad.leadId === identity.id),
    [squads, identity.id],
  );

  const issue = useCallback(
    (input: {
      squadId: string;
      kind: OrderKind;
      place?: string;
      note?: string;
      at?: { x: number; y: number };
    }) => {
      if (!doc) return;
      issueOrder(doc, {
        id: crypto.randomUUID(),
        squadId: input.squadId,
        kind: input.kind,
        place: input.place ?? "",
        note: input.note ?? "",
        status: "active",
        issuedBy: identity.id,
        issuedAt: Date.now(),
        mapId: activeMapId,
        ...(input.at ? { at: input.at } : {}),
      });
    },
    [doc, identity.id, activeMapId],
  );

  const complete = useCallback(
    (orderId: string) => {
      if (!doc) return;
      setOrderStatus(doc, orderId, "complete");
    },
    [doc],
  );

  const amend = useCallback(
    (
      orderId: string,
      changes: Partial<Pick<Order, "kind" | "place" | "note" | "at" | "squadId">>,
    ) => {
      if (!doc) return;
      amendOrder(doc, orderId, changes);
    },
    [doc],
  );

  const cancel = useCallback(
    (orderId: string) => {
      if (!doc) return;
      cancelOrder(doc, orderId);
    },
    [doc],
  );

  const clearVote = useCallback(() => {
    if (!doc) return;
    withdrawVote(doc, identity.id);
  }, [doc, identity.id]);

  // --- resolve votes and vacant chairs -----------------------------------

  useEffect(() => {
    if (!doc || !ready) return;

    const tick = (): void => {
      const now = Date.now();
      const state = readCommandState(doc);

      if (state.kind === "contested") {
        const currentVotes = readVotes(doc);
        const currentTally = tallyVotes(currentVotes, eligibleCount);
        // Resolve on expiry, or as soon as the result is already decided.
        if (isVoteExpired(state, now) || currentTally.wouldPass) {
          const outcome = applyCommandAction(
            { state, votes: currentVotes, eligibleCount },
            { type: "resolve", at: now },
          );
          writeCommandState(doc, outcome.state, outcome.clearVotes);
        }
        return;
      }

      if (state.kind === "held") {
        // A commander nobody has seen for a while releases the chair, so a
        // crash does not leave the team permanently leaderless.
        const stillHere = present.some((entry) => entry.id === state.commanderId);
        if (!stillHere) {
          const record = players.find((entry) => entry.id === state.commanderId);
          const lastSeen = record?.lastSeenAt ?? state.since;
          if (now - lastSeen >= COMMANDER_GRACE_MS) {
            const outcome = applyCommandAction(
              { state, votes: readVotes(doc), eligibleCount },
              { type: "commanderLost", at: now },
            );
            writeCommandState(doc, outcome.state, outcome.clearVotes);
          }
        }
      }

      // A squad lead nobody has seen in a while hands off too, and on the same
      // grace period as the command chair. Command already gets this right;
      // a squad stuck forever behind a lead who quit without clicking "Leave"
      // is the same failure with a smaller blast radius, not a different one.
      for (const squad of squads) {
        if (!squad.leadId) continue;
        if (present.some((entry) => entry.id === squad.leadId)) continue;

        const record = players.find((entry) => entry.id === squad.leadId);
        const lastSeen = record?.lastSeenAt ?? 0;
        if (now - lastSeen < COMMANDER_GRACE_MS) continue;

        const successor = players
          .filter((entry) => entry.squad === squad.id && entry.id !== squad.leadId)
          .sort((a, b) => {
            const aHere = present.some((entry) => entry.id === a.id) ? 0 : 1;
            const bHere = present.some((entry) => entry.id === b.id) ? 0 : 1;
            return aHere - bHere || a.joinedAt - b.joinedAt;
          })[0];
        setSquadLeadRecord(doc, squad.id, successor?.id ?? "");
      }
    };

    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [doc, ready, eligibleCount, present, players, squads]);

  return {
    code: code ?? "",
    ready,
    sync,
    meta,
    players,
    present,
    commandState,
    votes,
    markers,
    routes,
    orders,
    allOrders,
    allMarkers,
    allRoutes,
    squads,
    squadTags,
    commanderId,
    isCommander: commanderId === identity.id,
    tally,
    myVote,
    mySquad,
    myOrder,
    isSquadLead,
    claimCommand,
    contestCommand,
    resignCommand,
    vote,
    clearVote,
    setMap,
    setFaction,
    joinSquad,
    createSquad,
    setSquadLead,
    removePlayer,
    setVehicle,
    putMarker,
    deleteMarker,
    putRoute,
    deleteRoute,
    newMatch,
    issue,
    complete,
    amend,
    cancel,
    doc,
  };
}
